import React from "react";
import PropTypes from "prop-types";
import { MdPhoto, MdCancel } from "react-icons/md";
import Select from "react-select";

const FormField = ({
  type = "text",
  label,
  name,
  value,
  onChange,
  onPhotoChange,
  onPhotoUpload,
  onKeyDown,
  placeholder,
  required = false,
  rows = 4,
  options = [],
  photoPreview,
  onRemovePhoto,
  className = "",
  isMulti = false,
  onSelectChange,
  ...props
}) => {
  const baseInputClasses =
    "w-full rounded-md border border-gray-300 px-4 py-3 focus:border-[#2ca01c] focus:outline-none focus:ring-2 focus:ring-[#2ca01c]/20";

  // Styles personnalisés pour react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "48px",
      border: state.isFocused ? "2px solid #2ca01c" : "1px solid #d1d5db",
      borderRadius: "6px",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(44, 160, 28, 0.2)" : "none",
      "&:hover": {
        border: state.isFocused ? "2px solid #2ca01c" : "1px solid #9ca3af",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#2ca01c",
      borderRadius: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "white",
      fontSize: "14px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "white",
      "&:hover": {
        backgroundColor: "#238615",
        color: "white",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#2ca01c"
        : state.isFocused
        ? "#f0f9ff"
        : "white",
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: state.isSelected ? "#2ca01c" : "#f0f9ff",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9ca3af",
    }),
  };

  const renderInput = () => {
    switch (type) {
      case "textarea":
        return (
          <textarea
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            rows={rows}
            className={baseInputClasses}
            placeholder={placeholder}
            required={required}
            {...props}
          />
        );

      case "select":
        return (
          <select
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            className={baseInputClasses}
            required={required}
            {...props}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "react-select":
        return (
          <Select
            id={name}
            name={name}
            value={value}
            onChange={onSelectChange}
            options={options}
            isMulti={isMulti}
            placeholder={placeholder}
            styles={selectStyles}
            noOptionsMessage={() => "Aucune option disponible"}
            loadingMessage={() => "Chargement..."}
            className="react-select-container"
            classNamePrefix="react-select"
            {...props}
          />
        );

      case "file":
        return (
          <div>
            <div className="flex items-center">
              <button
                type="button"
                onClick={onPhotoUpload}
                onKeyDown={onKeyDown}
                tabIndex="0"
                aria-label="Choisir une photo"
                role="button"
                className="flex w-full cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#2ca01c]"
              >
                <MdPhoto className="mb-2 text-3xl text-gray-400" />
                <span className="text-sm text-gray-500">
                  Cliquez ou glissez une image
                </span>
              </button>
              <input
                type="file"
                id={name}
                name={name}
                onChange={onPhotoChange}
                accept="image/*"
                className="hidden"
                aria-hidden="true"
                {...props}
              />
            </div>
            {photoPreview && (
              <div className="mt-3 overflow-hidden rounded-md border border-gray-200">
                <div className="relative">
                  <img
                    src={photoPreview}
                    alt="Aperçu de l'image sélectionnée"
                    className="h-48 w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={onRemovePhoto}
                    className="absolute right-2 top-2 rounded-full bg-white/80 p-1 text-gray-700 hover:bg-white"
                    aria-label="Supprimer l'image"
                  >
                    <MdCancel />
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <input
            type={type}
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            className={baseInputClasses}
            placeholder={placeholder}
            required={required}
            {...props}
          />
        );
    }
  };

  return (
    <div className={className}>
      <label
        htmlFor={name}
        className="mb-1 block text-sm font-semibold text-gray-700"
      >
        {label} {required && <span className="text-[#e21b1b]">*</span>}
      </label>
      {renderInput()}
    </div>
  );
};

FormField.propTypes = {
  type: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onPhotoChange: PropTypes.func,
  onPhotoUpload: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  rows: PropTypes.number,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  photoPreview: PropTypes.string,
  onRemovePhoto: PropTypes.func,
  className: PropTypes.string,
  isMulti: PropTypes.bool,
  onSelectChange: PropTypes.func,
};

export default FormField;
